import { useState, useEffect, useRef, useCallback } from 'react'
import { X, AlertTriangle, CheckCircle, HelpCircle, Info } from 'lucide-react'

type DialogVariant = 'confirm' | 'danger' | 'success' | 'prompt' | 'primary'

interface ConfirmDialogProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (value?: string) => void
  title: string
  message: string
  confirmText?: string
  cancelText?: string
  variant?: DialogVariant
  showInput?: boolean
  inputLabel?: string
  inputPlaceholder?: string
  inputRequired?: boolean
}

const variantConfig = {
  confirm: {
    icon: HelpCircle,
    iconBg: 'bg-blue-100 dark:bg-blue-900/30',
    iconColor: 'text-blue-600 dark:text-blue-400',
    buttonClass: 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500',
  },
  danger: {
    icon: AlertTriangle,
    iconBg: 'bg-red-100 dark:bg-red-900/30',
    iconColor: 'text-red-600 dark:text-red-400',
    buttonClass: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
  },
  success: {
    icon: CheckCircle,
    iconBg: 'bg-green-100 dark:bg-green-900/30',
    iconColor: 'text-green-600 dark:text-green-400',
    buttonClass: 'bg-green-600 hover:bg-green-700 focus:ring-green-500',
  },
  prompt: {
    icon: HelpCircle,
    iconBg: 'bg-amber-100 dark:bg-amber-900/30',
    iconColor: 'text-amber-600 dark:text-amber-400',
    buttonClass: 'bg-amber-600 hover:bg-amber-700 focus:ring-amber-500',
  },
  primary: {
    icon: Info,
    iconBg: 'bg-primary-100 dark:bg-primary-900/30',
    iconColor: 'text-primary-600 dark:text-primary-400',
    buttonClass: 'bg-primary-600 hover:bg-primary-700 focus:ring-primary-500',
  },
}

export function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  variant = 'confirm',
  showInput = false,
  inputLabel,
  inputPlaceholder,
  inputRequired = false,
}: ConfirmDialogProps) {
  const [inputValue, setInputValue] = useState('')
  const [isAnimating, setIsAnimating] = useState(false)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  const config = variantConfig[variant]
  const Icon = config.icon

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true)
      setInputValue('')
      setTimeout(() => {
        inputRef.current?.focus()
      }, 100)
    }
  }, [isOpen])

  const handleClose = () => {
    setIsAnimating(false)
    setTimeout(onClose, 150)
  }

  const handleConfirm = () => {
    if (showInput && inputRequired && !inputValue.trim()) {
      inputRef.current?.focus()
      return
    }
    onConfirm(showInput ? inputValue : undefined)
    handleClose()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleClose()
    } else if (e.key === 'Enter' && !showInput) {
      handleConfirm()
    } else if (e.key === 'Enter' && e.ctrlKey && showInput) {
      handleConfirm()
    }
  }

  if (!isOpen) return null

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-all duration-150 ${
        isAnimating ? 'opacity-100' : 'opacity-0'
      }`}
      onKeyDown={handleKeyDown}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Dialog */}
      <div
        className={`relative bg-white dark:bg-slate-800 rounded-xl shadow-2xl max-w-md w-full transform transition-all duration-150 ${
          isAnimating ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        }`}
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="p-6">
          {/* Icon */}
          <div className={`mx-auto w-12 h-12 rounded-full ${config.iconBg} flex items-center justify-center mb-4`}>
            <Icon className={`h-6 w-6 ${config.iconColor}`} />
          </div>

          {/* Title */}
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white text-center mb-2">
            {title}
          </h3>

          {/* Message */}
          <p className="text-gray-600 dark:text-gray-300 text-center mb-6">
            {message}
          </p>

          {/* Input (optional) */}
          {showInput && (
            <div className="mb-6">
              {inputLabel && (
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {inputLabel}
                  {inputRequired && <span className="text-red-500 ml-1">*</span>}
                </label>
              )}
              <textarea
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder={inputPlaceholder}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none transition-shadow"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Ctrl + Enter para confirmar
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={handleClose}
              className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-slate-500 transition-colors"
            >
              {cancelText}
            </button>
            <button
              onClick={handleConfirm}
              className={`flex-1 px-4 py-2.5 text-white rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-slate-800 transition-colors ${config.buttonClass}`}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// Hook for easier dialog management
interface ConfirmOptions {
  title: string
  message: string
  confirmText?: string
  cancelText?: string
  variant?: DialogVariant
  showInput?: boolean
  inputLabel?: string
  inputPlaceholder?: string
  inputRequired?: boolean
}

interface UseConfirmDialogReturn {
  confirm: (options: ConfirmOptions) => Promise<boolean | string>
  dialogProps: ConfirmDialogProps
}

export function useConfirmDialog(): UseConfirmDialogReturn {
  const [isOpen, setIsOpen] = useState(false)
  const [options, setOptions] = useState<ConfirmOptions>({
    title: '',
    message: '',
  })
  const resolveRef = useRef<((value: boolean | string) => void) | null>(null)

  const confirm = useCallback((opts: ConfirmOptions): Promise<boolean | string> => {
    setOptions(opts)
    setIsOpen(true)
    return new Promise((resolve) => {
      resolveRef.current = resolve
    })
  }, [])

  const handleClose = useCallback(() => {
    setIsOpen(false)
    resolveRef.current?.(false)
    resolveRef.current = null
  }, [])

  const handleConfirm = useCallback((value?: string) => {
    setIsOpen(false)
    resolveRef.current?.(options.showInput ? value || '' : true)
    resolveRef.current = null
  }, [options.showInput])

  return {
    confirm,
    dialogProps: {
      isOpen,
      onClose: handleClose,
      onConfirm: handleConfirm,
      ...options,
    },
  }
}
